import { useEffect, useState } from "react";
import { Button, Loader } from "@/common/components/ui";
import InitialProcessRegistrationService from "@/services/NPS/Stepper/APIs/InitialProcessRegistrationService";
import { notify } from "@/common/components/common/Notification";
import { DatePickerInput } from "@/common/components/features";
interface InitialRegistrationModalProps {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  onSubmit: () => void;
  onFormSubmit?: (data: {
    pan: string;
    fullName: string;
    dob: string;
    mobile: string;
    email: string;
    code_reference: string;
  }) => Promise<void>;
  saveEmail: string | null;
}

const InitialRegistrationModal: React.FC<InitialRegistrationModalProps> = ({
  saveEmail,
  setIsOpen,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    pan: "",
    fullName: "",
    dob: "",
    mobile: "",
    email: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({
    pan: "",
    fullName: "",
    dob: "",
    mobile: "",
    email: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const { tenantId } = useTenant();
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({
    pan: "",
    name_as_per_pan: "",
    dob_as_per_pan: "",
    emailId: "",
    mobNumber: "",
  });

  const handleChange = (field: string, value: string) => {
    const input = document.activeElement as HTMLInputElement;
    const selectionStart = input?.selectionStart ?? 0;
    // const selectionEnd = input?.selectionEnd || 0;
    let newValue = value;

    // Convert PAN & Full Name to uppercase
    if (field === "pan" || field === "fullName") {
      newValue = value.toUpperCase();
    }

    setFormData(prev => ({
      ...prev,
      [field]: newValue,
    }));

    setTimeout(() => {
      if (input) {
        const diff = newValue.length - value.length;
        const cursorPos = selectionStart + diff;
        input.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);

    // Clear error when user types
    setErrors(prev => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!formData.pan) {
      newErrors.pan = "PAN is required.";
    }
    if (!formData.fullName) {
      newErrors.fullName = "Full Name is required.";
    }
    if (!formData.dob) {
      newErrors.dob = "Date of Birth is required.";
    }
    if (!formData.mobile) {
      newErrors.mobile = "Mobile Number is required.";
    }
    if (!formData.email) {
      newErrors.email = "Email ID is required.";
    }

    setErrors(prev => ({
      ...prev,
      ...newErrors,
    }));

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const payload = {
      pan: formData.pan,
      name_as_per_pan: formData.fullName,
      dob_as_per_pan: formData.dob,
      emailId: formData.email,
      mobNumber: formData.mobile,
    };

    try {
      setIsLoading(true);
      const response =
        await InitialProcessRegistrationService.initialProcessRegistration(
          payload
        );

      // Even if `success` is true, check for server-side errors
      if (response?.errors && Object.keys(response.errors).length > 0) {
        const mappedErrors: Record<string, string> = {};
        if (response.errors.pan) {
          mappedErrors.pan = response.errors.pan;
        }
        if (response.errors.pran) {
          mappedErrors.pran = response.errors.pran;
        }
        if (response.errors.name_as_per_pan) {
          mappedErrors.fullName = response.errors.name_as_per_pan;
        }
        if (response.errors.dob_as_per_pan) {
          mappedErrors.dob = response.errors.dob_as_per_pan;
        }
        if (response.errors.emailId) {
          mappedErrors.email = response.errors.emailId;
        }
        if (response.errors.mobNumber) {
          mappedErrors.mobile = response.errors.mobNumber;
        }
        setApiErrors(mappedErrors);
        notify.error("Validation failed. Please correct the errors below.");
        return;
      }

      // Truly successful: no errors
      if (response?.success) {
        const serialized = JSON.stringify(payload);
        localStorage.setItem("initialRegistration", serialized);
        notify.success("Registration successful!");
        setApiErrors({});
        // Now, call the EmailSendOtpService to send OT
        setIsOpen(false);
        onSubmit();
      } else {
        notify.error("Registration failed. Please try again.");
      }
    } catch (error) {
      notify.error("Failed to submit registration. Please try again.");
      console.error("Registration Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (saveEmail) {
      setFormData(prev => ({
        ...prev,
        email: saveEmail,
      }));
    }
  }, [saveEmail]);

  useEffect(() => {
    const userInfo = localStorage.getItem("userInfo");
    const mobile = userInfo ? JSON.parse(userInfo).phone_number : "";
    if (mobile) {
      setFormData(prev => ({
        ...prev,
        mobile: mobile,
      }));
    }
  }, []);

  const handleTermsAndConditions = () => {
    const url = `${window.location.origin}/terms-and-conditions`;
    window.open(url, "_blank");
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-10 space-y-3 rounded-md bg-white p-4 md:mt-3 md:space-y-5 md:p-6"
    >
      {isLoading && <Loader message="Submitting your registration..." />}
      {/* Title */}
      <h2 className="font-sans text-[20px] font-bold text-[#1D2C56] md:text-[30px] md:text-[35px]">
        Initial Process of Registration
      </h2>

      {/* Error Message Block */}
      {Object.keys(apiErrors).length > 0 && (
        <div className="mb-4 space-y-2">
          {Object.values(apiErrors).map(error => (
            <p key={error} className="text-sm text-red-500">
              {error}
            </p>
          ))}
        </div>
      )}

      {/* PAN */}
      <div className="flex flex-col">
        <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
          Permanent Account Number (PAN)
          <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="Permanent Account Number (PAN)"
          maxLength={10}
          value={formData.pan}
          onChange={e => handleChange("pan", e.target.value)}
          className={`w-full rounded-md border ${
            errors.pan ? "border-red-500" : "border-[#DFE7EC]"
          } bg-white px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]`}
        />
        {errors.pan && (
          <p className="mt-1 font-sans text-sm font-semibold text-red-500">
            {errors.pan}
          </p>
        )}
      </div>

      {/* Full Name */}
      <div className="flex flex-col">
        <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
          Full Name (As per PAN Card)
          <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="Full Name (As per PAN Card)"
          value={formData.fullName}
          onChange={e => handleChange("fullName", e.target.value)}
          className={`w-full rounded-md border ${
            errors.fullName ? "border-red-500" : "border-[#DFE7EC]"
          } bg-white px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]`}
        />
        {errors.fullName && (
          <p className="mt-1 font-sans text-sm font-semibold text-red-500">
            {errors.fullName}
          </p>
        )}
      </div>

      <div className="flex flex-col">
        <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
          Birth Date (DD/MM/YYYY)
          <span className="text-red-500">*</span>
        </label>
        <DatePickerInput
          value={formData.dob}
          onChange={val => handleChange("dob", val)}
          error={errors.dob}
        />
      </div>

      {/* Mobile */}
      <div className="flex flex-col">
        <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
          Mobile Number
          <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          placeholder="Mobile Number"
          maxLength={10}
          value={formData.mobile}
          readOnly
          onChange={e =>
            handleChange("mobile", e.target.value.replace(/[^0-9]/g, ""))
          }
          className={`w-full rounded-md border ${
            errors.mobile ? "border-red-500" : "border-[#DFE7EC]"
          } w-full rounded-md border border-[#DFE7EC] bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none`}
        />
        {errors.mobile && (
          <p className="mt-1 font-sans text-sm font-semibold text-red-500">
            {errors.mobile}
          </p>
        )}
      </div>

      {/* Email */}
      <div className="flex flex-col">
        <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
          Email ID
          <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          placeholder="Email ID"
          value={formData.email}
          readOnly
          onChange={e => handleChange("email", e.target.value)}
          className={`w-full rounded-md border ${
            errors.email ? "border-red-500" : "border-[#DFE7EC]"
          } bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none`}
        />
        {errors.email && (
          <p className="mt-1 font-sans text-sm font-semibold text-red-500">
            {errors.email}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="terms"
          checked={accepted}
          onChange={e => setAccepted(e.target.checked)}
          className="h-5 w-5 cursor-pointer rounded border-[#000] bg-[#f2f2f2] text-[#000] accent-[#000] focus:ring-0"
        />
        <button
          onClick={handleTermsAndConditions}
          className="cursor-pointer font-sans text-[18px] font-semibold text-[#707070] hover:underline"
        >
          Accept terms and conditions
        </button>
      </div>

      {/* Submit Button */}
      <div className="mt-8 flex justify-center pt-3">
        <Button
          type="submit"
          disabled={!accepted} // ✅ Disable until checked
          className={`h-[50px] w-[180px] rounded-md py-2 font-sans text-[18px] font-semibold text-white transition ${
            accepted
              ? "cursor-pointer bg-gradient-to-r from-[#127B8D] to-[#10B981] hover:opacity-95"
              : "cursor-not-allowed bg-gray-400"
          }`}
        >
          Submit
        </Button>
      </div>
    </form>
  );
};

export default InitialRegistrationModal;
